import { Add, Delete } from '@mui/icons-material';
import { Box, Typography, Paper, Button, IconButton, Divider } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';

import FormSkeleton from './formSkeleton';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { StepNavigation } from 'components/v2/StepNavigation';

import { BooleanValue } from 'constant';
import {
    useCountries,
    useRegions,
    useBusinessSectors,
    useBusinessOwnership,
    useSaveBusinessOwnership,
} from 'hooks';
import useTranslate from 'hooks/useTranslate';
import type { BusinessOwnership } from 'types';
import { generateOptionFromObject, generateOptionsFromString } from 'utils/dataFormatter';
import { isDestinationCountry } from 'utils/countries';
import { businessOwnershipValidationSchema } from 'utils/validations/businessOwnershipSchema';

const booleanOptions = generateOptionFromObject<boolean>(BooleanValue);

const emptyBusiness: Partial<BusinessOwnership> = {
    annual_sales: 0,
    city: undefined,
    country: '',
    currency: '',
    current: false,
    end: '',
    net_company_value: 0,
    net_profit: 0,
    number_employees: 0,
    percent_ownership: 0,
    region: undefined,
    start: '',
    type: '',
};

interface BusinessOwnershipStepProps {
    formData: Record<string, unknown>;
    isLastStep: boolean;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
    onSubmit: (data: Record<string, unknown>) => void;
}

const BusinessOwnershipStep = ({ onBack, onSubmit, isLastStep }: BusinessOwnershipStepProps) => {
    const { translate } = useTranslate();
    const { data: businessData, isLoading } = useBusinessOwnership();
    const saveBusiness = useSaveBusinessOwnership();

    const { data: businessSectors } = useBusinessSectors();
    const { data: countries = [] } = useCountries();
    const { data: regions } = useRegions();

    const businessTypeOptions = generateOptionsFromString(businessSectors || []);
    const countryOptions = generateOptionsFromString(countries || []);

    const initialValues = {
        has_business: Boolean(businessData && businessData.length > 0),
        businesses: businessData || [],
    };

    const handleSubmit = async (values: {
        has_business?: boolean;
        businesses: BusinessOwnership[];
    }) => {
        try {
            const payload = values.has_business ? values.businesses || [] : [];
            await saveBusiness.mutateAsync(payload);
            onSubmit({ businesses: payload });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (isLoading) {
        return <FormSkeleton fields={12} />;
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={businessOwnershipValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, isValid, isSubmitting, setFieldValue, setValues }) => {
                    const isSaving = isSubmitting || saveBusiness.isPending;

                    return (
                        <FormBusyProvider busy={isSaving}>
                            <Form>
                                <FormField
                                    type="radio"
                                    row
                                    name="has_business"
                                    label="doYouOwnBusiness"
                                    options={booleanOptions}
                                    onChange={(value: unknown) => {
                                        const boolValue = value === true || value === 'true';
                                        return setValues(
                                            {
                                                ...values,
                                                has_business: boolValue,
                                                businesses: boolValue
                                                    ? [{ ...emptyBusiness } as BusinessOwnership]
                                                    : [],
                                            },
                                            true
                                        );
                                    }}
                                />

                                <FieldArray name="businesses">
                                    {({ push, remove }) => (
                                        <Box>
                                            {values.has_business &&
                                                values.businesses.map((business, index) => {
                                                    const showRegionCity = isDestinationCountry(
                                                        business.country
                                                    );
                                                    const regionOptions = generateOptionsFromString(
                                                        showRegionCity && business.country
                                                            ? (
                                                                  regions as Record<
                                                                      string,
                                                                      string[]
                                                                  > | null
                                                              )?.[business.country] || []
                                                            : []
                                                    );

                                                    return (
                                                        <Paper
                                                            key={index}
                                                            elevation={1}
                                                            sx={{
                                                                p: { xs: 2, sm: 3 },
                                                                mb: 1,
                                                                mt: 2,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    mb: 2,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{ fontSize: '1.1rem' }}
                                                                >
                                                                    Business {index + 1}
                                                                </Typography>
                                                                {values.businesses.length > 1 && (
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            remove(index)
                                                                        }
                                                                        color="error"
                                                                        size="small"
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                            <Divider sx={{ mb: 2 }} />

                                                            <FormGridLayout>
                                                                <FormField
                                                                    required
                                                                    type="radio"
                                                                    row
                                                                    name={`businesses.${index}.current`}
                                                                    label="current"
                                                                    options={booleanOptions}
                                                                    onChange={(value: unknown) => {
                                                                        const boolValue =
                                                                            value === 'true' ||
                                                                            value === true;
                                                                        setFieldValue(
                                                                            `businesses.${index}.current`,
                                                                            boolValue
                                                                        );
                                                                        if (boolValue) {
                                                                            setFieldValue(
                                                                                `businesses.${index}.end`,
                                                                                ''
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                                <FormField
                                                                    required
                                                                    type="date"
                                                                    name={`businesses.${index}.start`}
                                                                    label="startDate"
                                                                />
                                                                <FormField
                                                                    required
                                                                    type="date"
                                                                    name={`businesses.${index}.end`}
                                                                    label="endDate"
                                                                    disabled={business.current}
                                                                />
                                                                <FormField
                                                                    required
                                                                    type="autosuggest"
                                                                    name={`businesses.${index}.type`}
                                                                    label="businessType"
                                                                    options={businessTypeOptions}
                                                                />
                                                                <FormField
                                                                    required
                                                                    type="autosuggest"
                                                                    name={`businesses.${index}.country`}
                                                                    label="country"
                                                                    options={countryOptions}
                                                                    onChange={(value) => {
                                                                        setFieldValue(
                                                                            `businesses.${index}.country`,
                                                                            value
                                                                        );
                                                                        setFieldValue(
                                                                            `businesses.${index}.region`,
                                                                            ''
                                                                        );
                                                                        if (
                                                                            !isDestinationCountry(
                                                                                String(value || '')
                                                                            )
                                                                        ) {
                                                                            setFieldValue(
                                                                                `businesses.${index}.city`,
                                                                                ''
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                                {showRegionCity && (
                                                                    <>
                                                                        <FormField
                                                                            required
                                                                            type="autosuggest"
                                                                            name={`businesses.${index}.region`}
                                                                            label="region"
                                                                            options={regionOptions}
                                                                            disabled={
                                                                                !business.country
                                                                            }
                                                                        />
                                                                        <FormField
                                                                            name={`businesses.${index}.city`}
                                                                            label="city"
                                                                        />
                                                                    </>
                                                                )}
                                                                <FormField
                                                                    required
                                                                    name={`businesses.${index}.percent_ownership`}
                                                                    label="shareOfCompany"
                                                                    type="number"
                                                                />
                                                                <FormField
                                                                    required
                                                                    name={`businesses.${index}.number_employees`}
                                                                    label="numberOfEmployees"
                                                                    type="number"
                                                                />
                                                                <FormField
                                                                    required
                                                                    name={`businesses.${index}.annual_sales`}
                                                                    label="annualSales(USD)"
                                                                    type="number"
                                                                />
                                                                <FormField
                                                                    required
                                                                    name={`businesses.${index}.net_profit`}
                                                                    label="netProfit(USD)"
                                                                    type="number"
                                                                />
                                                                <FormField
                                                                    required
                                                                    name={`businesses.${index}.net_company_value`}
                                                                    label="netCompanyValue(USD)"
                                                                    type="number"
                                                                />
                                                            </FormGridLayout>
                                                        </Paper>
                                                    );
                                                })}

                                            {values.has_business && (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<Add />}
                                                    onClick={() =>
                                                        push({
                                                            ...emptyBusiness,
                                                        } as BusinessOwnership)
                                                    }
                                                    fullWidth
                                                    sx={{ mb: 1, mt: 2 }}
                                                >
                                                    {translate('addBusiness')}
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </FieldArray>

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isSaving}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default BusinessOwnershipStep;
